import React from "react";
import "./ErrorBoundary.css";

/**
 * ErrorBoundary Component
 * 
 * Industry-standard error boundary with:
 * - User-friendly fallback UI
 * - Error logging capability
 * - Reset functionality
 * - Customizable fallback rendering
 * 
 * Usage:
 * <ErrorBoundary fallbackType="page">
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Store error details
    this.setState({
      error,
      errorInfo,
    });

    // TODO: Log to error reporting service (e.g., Sentry, LogRocket, Bugsnag)
    // Example:
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call custom onReset if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided by parent
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Render different fallback based on type
      const { fallbackType = "default" } = this.props;

      if (fallbackType === "page") {
        return <PageErrorFallback onReset={this.handleReset} />;
      }

      if (fallbackType === "section") {
        return <SectionErrorFallback onReset={this.handleReset} />;
      }

      if (fallbackType === "inline") {
        return <InlineErrorFallback onReset={this.handleReset} />;
      }

      // Default fallback
      return <PageErrorFallback onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

/**
 * Full-page error fallback
 * Used for critical errors (top-level boundaries)
 */
function PageErrorFallback({ onReset }) {
  return (
    <div className="error-boundary-page">
      <div className="error-boundary-content">
        <div className="error-icon">⚠️</div>
        <h1 className="error-title">Something went wrong</h1>
        <p className="error-message">
          We encountered an unexpected error. Your work has been preserved.
        </p>
        <div className="error-actions">
          <button className="btn" onClick={onReset}>
            Try Again
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>
        <p className="error-help">
          If the problem persists, try refreshing your browser or{" "}
          <a href="/projects" className="error-link">
            return to home
          </a>
          .
        </p>
      </div>
    </div>
  );
}

/**
 * Section error fallback
 * Used for feature sections (questions list, comments, etc.)
 */
function SectionErrorFallback({ onReset }) {
  return (
    <div className="error-boundary-section">
      <div className="error-boundary-content">
        <div className="error-icon-small">⚠️</div>
        <p className="error-message-small">
          This section failed to load
        </p>
        <button className="btn btn-sm" onClick={onReset}>
          Try Again
        </button>
      </div>
    </div>
  );
}

/**
 * Inline error fallback
 * Used for small components (individual items, cards, etc.)
 */
function InlineErrorFallback({ onReset }) {
  return (
    <div className="error-boundary-inline">
      <span className="error-text">Failed to load</span>
      <button className="error-retry-link" onClick={onReset}>
        Retry
      </button>
    </div>
  );
}

export default ErrorBoundary;
